// @ts-check
/* eslint-env browser */

import { render, html } from 'uhtml'
import { reactiveStore } from '../../reactive-store.js'
import './entity-list-item.js'

/**
 * Web component for rendering a list of entities from reactiveStore.features.
 * Filters features by entity type and renders entity-list-item components.
 *
 * Usage:
 *   <entity-list entity-type="tree" job-id="job-123"></entity-list>
 */
export class EntityList extends HTMLElement {
  /** @type {string | null} */
  #entityType
  /** @type {string} */
  #jobId
  /** @type {string | null} */
  #featureType
  /** @type {(() => void) | null} */
  #renderCallback = null

  connectedCallback () {
    this.#entityType = this.getAttribute('entity-type')
    this.#jobId = this.getAttribute('job-id') || ''
    // Map entity-type to feature_type
    this.#featureType = this.#entityType === 'treepreservationzone' ? 'tree_preservation_zone' : this.#entityType

    reactiveStore.features.subscribe(() => {
      this.#render()
    })
    this.#render()
  }

  /**
   * Sorts entities by number extracted from name (same logic as sortEntitiesByNumber)
   * @param {any[]} entities
   * @returns {any[]}
   */
  #sortEntitiesByNumber (entities) {
    return [...entities].sort((a, b) => {
      const nameA = a?.properties?.name || ''
      const nameB = b?.properties?.name || ''

      const numA = parseInt(nameA.substring(0, 6).match(/\d+/)?.[0] ?? 'NaN', 10)
      const numB = parseInt(nameB.substring(0, 6).match(/\d+/)?.[0] ?? 'NaN', 10)

      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB
      }

      if (!isNaN(numA) && isNaN(numB)) {
        return -1
      }

      if (isNaN(numA) && !isNaN(numB)) {
        return 1
      }

      return nameA.localeCompare(nameB)
    })
  }

  /**
   * Transforms a feature from reactiveStore to the format expected by entity-list-item
   * @param {any} feature
   * @param {string} entityType
   * @returns {any}
   */
  #transformFeature (feature, entityType) {
    return {
      properties: {
        _id: feature.properties?._id || 'main',
        name: feature.properties?.name || '',
        reviewed: feature.properties?.reviewed,
        feature_type: feature.properties?.feature_type,
        ...feature.properties // Include all properties
      },
      geometry: feature.geometry,
      type: feature.type || entityType
    }
  }

  /**
   * Renders the component
   */
  #render () {
    if (!this.#entityType) {
      return
    }

    const features = reactiveStore.features.get().filter(feature => !feature?.properties?._isMapOverlay)

    // Filtering is only needed for TPOs (to separate trees from zones)
    // For Article 4 areas and Conservation areas, all extracted objects are areas - no filtering needed
    let filteredFeatures
    if (this.#entityType === 'tree' || this.#entityType === 'treepreservationzone') {
      // Filter features by feature_type, with geometry type fallback for legacy data
      filteredFeatures = features.filter(feature => {
        const featureType = feature.properties?.feature_type

        // Primary filter: match by feature_type (including features with null geometry)
        if (featureType === this.#featureType) {
          return true
        }

        // Fallback for legacy data without feature_type
        // Only filter by geometry type if geometry exists
        if (!featureType && feature.geometry?.coordinates?.length > 0) {
          if (this.#featureType === 'tree' && feature.geometry.type === 'Point') {
            return true
          }
          if (this.#featureType === 'tree_preservation_zone' && feature.geometry.type === 'Polygon') {
            return true
          }
        }

        return false
      })
    } else {
      filteredFeatures = features
    }

    // Sort features
    const sortedFeatures = this.#sortEntitiesByNumber(filteredFeatures)

    // Transform features for entity-list-item
    const transformedFeatures = sortedFeatures.map(feature =>
      this.#transformFeature(feature, this.#entityType || '')
    )

    render(this, html`
      <ul class="entity-list govuk-!-margin-0 govuk-!-padding-0">
        ${transformedFeatures.map(feature => html`
          <li>
            <entity-list-item
              feature='${JSON.stringify(feature)}'
              job-id="${this.#jobId}">
            </entity-list-item>
          </li>
        `)}
      </ul>
    `)
  }
}

customElements.define('entity-list', EntityList)
